/**
 * Settings Registry — single source of truth for all app settings.
 *
 * Every setting is defined ONCE with its localStorage key, default value,
 * and sync behavior (cloud vs local). This eliminates the inconsistent
 * hardcoded keysToSync arrays that previously existed across CloudSettings,
 * useCloudGatewaySync, and App.tsx.
 *
 * Key fix: `openai-api-key` is now `sync: 'cloud'`, so it syncs across
 * devices and Whisper STT works on new devices immediately.
 */

// ── Types ──

export interface SettingDef {
  key: string;
  defaultValue: string;
  sync: 'cloud' | 'local';
}

// ── Registry ──

export const SETTINGS_REGISTRY: Record<string, SettingDef> = {
  // ── Cloud-synced settings ──
  'voice-ai-gateways': {
    key: 'voice-ai-gateways',
    defaultValue: '[]',
    sync: 'cloud',
  },
  'voice-ai-active-gateway': {
    key: 'voice-ai-active-gateway',
    defaultValue: '',
    sync: 'cloud',
  },
  'voice-ai-speed': {
    key: 'voice-ai-speed',
    defaultValue: '1',
    sync: 'cloud',
  },
  'voice-ai-model': {
    key: 'voice-ai-model',
    defaultValue: 'default',
    sync: 'cloud',
  },
  'openai-api-key': {
    key: 'openai-api-key',
    defaultValue: '',
    sync: 'cloud',
  },
  'voice-ai-stt-engine': {
    key: 'voice-ai-stt-engine',
    defaultValue: '',
    sync: 'cloud',
  },
  'voice-ai-tts-provider': {
    key: 'voice-ai-tts-provider',
    defaultValue: 'elevenlabs',
    sync: 'cloud',
  },
  'voice-ai-edge-tts-url': {
    key: 'voice-ai-edge-tts-url',
    defaultValue: '',
    sync: 'cloud',
  },
  'voice-ai-vad-threshold': {
    key: 'voice-ai-vad-threshold',
    defaultValue: '0.8',
    sync: 'cloud',
  },
  'voice-ai-vad-adaptive': {
    key: 'voice-ai-vad-adaptive',
    defaultValue: 'true',
    sync: 'cloud',
  },
  'voice-ai-voice-interrupt': {
    key: 'voice-ai-voice-interrupt',
    defaultValue: 'false',
    sync: 'cloud',
  },
  'showThinkingStream': {
    key: 'showThinkingStream',
    defaultValue: 'true',
    sync: 'cloud',
  },
  'subagent-bubble-mode': {
    key: 'subagent-bubble-mode',
    defaultValue: 'follow',
    sync: 'cloud',
  },
  'subagent-depth': {
    key: 'subagent-depth',
    defaultValue: '0',
    sync: 'cloud',
  },
  'talkyn-session-quick-switch-side': {
    key: 'talkyn-session-quick-switch-side',
    defaultValue: 'right',
    sync: 'cloud',
  },
  'talkyn-session-switch-readout': {
    key: 'talkyn-session-switch-readout',
    defaultValue: 'true',
    sync: 'cloud',
  },
  'talkyn-send-while-running-mode': {
    key: 'talkyn-send-while-running-mode',
    defaultValue: 'queue',
    sync: 'cloud',
  },

  // ── Language & Voice (cloud-synced) ──
  'talkyn-preferred-languages': {
    key: 'talkyn-preferred-languages',
    defaultValue: '["en"]',
    sync: 'cloud',
  },
  'talkyn-also-reply-in': {
    key: 'talkyn-also-reply-in',
    defaultValue: '[]',
    sync: 'cloud',
  },
  'talkyn-voice-gender': {
    key: 'talkyn-voice-gender',
    defaultValue: 'female',
    sync: 'cloud',
  },
  'talkyn-voice-overrides': {
    key: 'talkyn-voice-overrides',
    defaultValue: '{}',
    sync: 'cloud',
  },

  // ── Local-only settings ──
  'voice-ai-mic-muted': {
    key: 'voice-ai-mic-muted',
    defaultValue: 'false',
    sync: 'local',
  },
  'voice-ai-speaker-muted': {
    key: 'voice-ai-speaker-muted',
    defaultValue: 'false',
    sync: 'local',
  },
  'voice-ai-spoken': {
    key: 'voice-ai-spoken',
    defaultValue: '[]',
    sync: 'local',
  },
  'voice-ai-debug-logging': {
    key: 'voice-ai-debug-logging',
    defaultValue: 'true',
    sync: 'local',
  },
  'voice-ai-tts-debug': {
    key: 'voice-ai-tts-debug',
    defaultValue: 'false',
    sync: 'local',
  },
  'voice-ai-edge-tts-token': {
    key: 'voice-ai-edge-tts-token',
    defaultValue: '',
    sync: 'local',
  },
  'voice-ai-upload-backend-url': {
    key: 'voice-ai-upload-backend-url',
    defaultValue: '',
    sync: 'local',
  },
  'voice-ai-upload-auth-token': {
    key: 'voice-ai-upload-auth-token',
    defaultValue: '',
    sync: 'local',
  },
  'talkyn-backend-url': {
    key: 'talkyn-backend-url',
    defaultValue: '',
    sync: 'local',
  },
  'talkyn-cf-uploads-domain': {
    key: 'talkyn-cf-uploads-domain',
    defaultValue: '',
    sync: 'local',
  },
  'talkyn-env-override': {
    key: 'talkyn-env-override',
    defaultValue: '',
    sync: 'local',
  },

  // ── Voice Lock (local-only — profile is device-specific) ──
  'voice-lock-enabled': {
    key: 'voice-lock-enabled',
    defaultValue: 'false',
    sync: 'local',
  },
  'voice-lock-profile': {
    key: 'voice-lock-profile',
    defaultValue: '',
    sync: 'local',
  },
  'voice-lock-trained-at': {
    key: 'voice-lock-trained-at',
    defaultValue: '',
    sync: 'local',
  },
};

// ── Accessors ──

/** Get a setting value from localStorage, falling back to its registered default. */
export function getSetting(key: string): string {
  const stored = localStorage.getItem(key);
  if (stored !== null) return stored;
  const def = SETTINGS_REGISTRY[key];
  return def ? def.defaultValue : '';
}

/** Set a setting value in localStorage. */
export function setSetting(key: string, value: string): void {
  localStorage.setItem(key, value);
}

/** Remove a setting from localStorage. */
export function removeSetting(key: string): void {
  localStorage.removeItem(key);
}

// ── Cloud Sync Helpers ──

/** Get all registry keys that should sync to the cloud. */
export function getCloudSyncKeys(): string[] {
  return Object.values(SETTINGS_REGISTRY)
    .filter((def) => def.sync === 'cloud')
    .map((def) => def.key);
}

/**
 * Collect all cloud-synced settings from localStorage, ready for PUT to the API.
 *
 * Includes:
 * 1. All registry keys with `sync: 'cloud'`
 * 2. Dynamic `voice-app-device-{id}` keypair keys for each gateway
 */
export function collectCloudSettings(): Record<string, string | null> {
  const settings: Record<string, string | null> = {};

  // Collect all cloud-synced registry keys
  for (const key of getCloudSyncKeys()) {
    settings[key] = localStorage.getItem(key);
  }

  // Collect device keypair keys for each gateway
  const gatewaysRaw = localStorage.getItem('voice-ai-gateways');
  if (gatewaysRaw) {
    try {
      const gateways = JSON.parse(gatewaysRaw);
      if (Array.isArray(gateways)) {
        for (const gw of gateways) {
          if (gw.id) {
            const keypairKey = `voice-app-device-${gw.id}`;
            settings[keypairKey] = localStorage.getItem(keypairKey);
          }
        }
      }
    } catch { /* ignore parse errors */ }
  }

  return settings;
}

/**
 * Restore cloud settings into localStorage from a GET /api/user/settings response.
 *
 * Merge rules:
 * - `voice-ai-gateways`: merge cloud gateways into local (only add gateways whose URL doesn't exist locally)
 * - `voice-ai-active-gateway`: only set if not already present locally
 * - `voice-app-device-{id}` keys: only set if not already present locally
 * - Everything else: overwrite localStorage
 *
 * @returns The number of new gateways added from cloud (0 = no new ones).
 */
export function restoreCloudSettings(settings: Record<string, string | null>): number {
  if (!settings || typeof settings !== 'object') return 0;

  let newGatewaysAdded = 0;

  // ── Merge gateway configs ──
  const cloudGatewaysRaw = settings['voice-ai-gateways'];
  if (cloudGatewaysRaw) {
    try {
      const cloudGateways = JSON.parse(String(cloudGatewaysRaw));
      if (Array.isArray(cloudGateways)) {
        const localGatewaysRaw = localStorage.getItem('voice-ai-gateways');
        const localGateways: Array<{ id?: string; url?: string; [k: string]: unknown }> = [];
        if (localGatewaysRaw) {
          try {
            const parsed = JSON.parse(localGatewaysRaw);
            if (Array.isArray(parsed)) localGateways.push(...parsed);
          } catch { /* ignore */ }
        }

        const localUrls = new Set(localGateways.map((g) => g.url).filter(Boolean));
        const merged = [...localGateways];

        for (const cloudGw of cloudGateways) {
          if (cloudGw.url && !localUrls.has(cloudGw.url)) {
            merged.push(cloudGw);
            localUrls.add(cloudGw.url);
            newGatewaysAdded++;
          }
        }

        localStorage.setItem('voice-ai-gateways', JSON.stringify(merged));
      }
    } catch { /* ignore parse errors */ }
  }

  // ── Apply non-gateway settings ──
  for (const [key, value] of Object.entries(settings)) {
    if (key === 'voice-ai-gateways') continue; // Already handled above
    if (value === null || value === undefined) continue;

    // Device keypair keys: only write if not already present locally
    if (key.startsWith('voice-app-device-')) {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, String(value));
      }
      continue;
    }

    // Active gateway ID: only set if not already present locally
    if (key === 'voice-ai-active-gateway') {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, String(value));
      }
      continue;
    }

    // Everything else: overwrite
    localStorage.setItem(key, String(value));
  }

  return newGatewaysAdded;
}
